// PROTOTYPE — keyword-based takeoff parser + surface suggester.
// The real engine will learn from saved mappings; this is a fixed rule list.

const UNIT_MAP = { "SQ FT": "SF", SF: "SF", "LN FT": "LF", LF: "LF", EA: "EA" };

const has = (name, re) => re.test(name);

// First matching rule wins, so specific rules (epoxy, level 5) come first.
const RULES = [
  { test: (n) => has(n, /\bCMU\b/i) && has(n, /epoxy|PCE-/i), surfaceId: "walls-cmu-epoxy", confidence: "High" },
  { test: (n) => has(n, /\bCMU\b/i), surfaceId: "walls-cmu", confidence: "High" },
  { test: (n) => has(n, /gyp|gwb|drywall/i) && has(n, /ceiling|soffit/i) && has(n, /epoxy|PCE-/i), surfaceId: "ceiling-gyp-epoxy", confidence: "High" },
  { test: (n) => has(n, /gyp|gwb/i) && has(n, /level 5/i), surfaceId: "wallcover-l5", confidence: "Medium" },
  { test: (n) => has(n, /gyp|gwb/i) && has(n, /epoxy|PCE-/i), surfaceId: "walls-gyp-epoxy", confidence: "High" },
  { test: (n) => has(n, /gyp|gwb/i) && has(n, /ceiling/i), surfaceId: "ceiling-gyp", confidence: "High" },
  { test: (n) => has(n, /gyp|gwb/i) && has(n, /soffit/i), surfaceId: "ceiling-gyp", confidence: "Medium" },
  { test: (n) => has(n, /gyp|gwb|drywall/i), surfaceId: "walls-gyp", confidence: "High" },
  { test: (n) => has(n, /\bACT\b|acoustic/i), surfaceId: "act", confidence: "High" },
  { test: (n) => has(n, /textured ceiling/i), surfaceId: "ceiling-textured", confidence: "Medium" },
  { test: (n) => has(n, /exposed structure/i), surfaceId: "exposed-structure", confidence: "High" },
  { test: (n) => has(n, /open deck|steel joist/i), surfaceId: "deck-dryfall", confidence: "Medium" },
  { test: (n) => has(n, /\bHM\b|hollow metal/i) && has(n, /borrowed lite/i), surfaceId: "frame-hm", confidence: "Medium" },
  { test: (n) => has(n, /\bHM\b|hollow metal/i) && has(n, /frame/i), surfaceId: "frame-hm", confidence: "High" },
  { test: (n) => has(n, /\bHM\b|hollow metal/i) && has(n, /exterior/i), surfaceId: "door-hm", confidence: "Medium" },
  { test: (n) => has(n, /\bHM\b|hollow metal/i) && has(n, /door/i), surfaceId: "door-hm", confidence: "High" },
  { test: (n) => has(n, /wood door/i), surfaceId: "door-wood", confidence: "High" },
  { test: (n) => has(n, /wood frame/i), surfaceId: "frame-wood", confidence: "High" },
  { test: (n) => has(n, /wood base|baseboard/i), surfaceId: "trim-base", confidence: "High" },
  { test: (n) => has(n, /chair rail/i), surfaceId: "trim-base", confidence: "Low" },
  { test: (n) => has(n, /window stool/i), surfaceId: "window-wood", confidence: "Low" },
  { test: (n) => has(n, /handrail|pipe rail/i), surfaceId: "handrail", confidence: "High" },
  { test: (n) => has(n, /guardrail/i), surfaceId: "handrail", confidence: "Medium" },
  { test: (n) => has(n, /hardie/i) && has(n, /soffit/i), surfaceId: "soffit-fascia", confidence: "Medium" },
  { test: (n) => has(n, /hardie/i) && has(n, /trim/i), surfaceId: "ext-trim-hardie", confidence: "High" },
  { test: (n) => has(n, /hardie/i) && has(n, /siding/i), surfaceId: "ext-siding-hardie", confidence: "High" },
];

/** Parses `(h=9'0")` → 9, `(h=8'6")` → 8.5. */
export const parseHeightFt = (name) => {
  const m = name.match(/h=(\d+)'(\d+)"/i);
  return m ? Number(m[1]) + Number(m[2]) / 12 : null;
};

export const parseSpecCode = (name) => {
  const m = name.match(/\b(PCE-\d+|P-\d+|DF-\d+|EXT-\d+)\b/);
  return m ? m[1] : null;
};

/**
 * @param {{id:string, section:string, rawName:string, location:string, qty:number|null, rawUnit:string}} row
 * @returns {import("../types").ParsedTakeoffLine}
 */
export const parseRawLine = (row) => {
  const unit = UNIT_MAP[row.rawUnit] || null;
  const warnings = [];
  if (row.qty == null || row.qty <= 0) warnings.push("No quantity");
  if (!unit) warnings.push(`Unknown unit "${row.rawUnit}"`);

  const rule = RULES.find((r) => r.test(row.rawName));
  return {
    ...row,
    unit,
    heightFt: parseHeightFt(row.rawName),
    specCode: parseSpecCode(row.rawName),
    warnings,
    suggestedSurfaceId: rule?.surfaceId || null,
    confidence: rule?.confidence || "None",
  };
};

/**
 * Remembered mappings ("Remember these mappings for future jobs") win over
 * rules: exact raw-name match → High confidence.
 */
export const applyRememberedMappings = (lines, remembered = {}) =>
  lines.map((l) =>
    remembered[l.rawName] ? { ...l, suggestedSurfaceId: remembered[l.rawName], confidence: "High" } : l
  );
